import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { BookOpen, FileText, Download, Search, ChevronDown, ChevronUp } from "lucide-react";

interface Booking {
  id: string;
  campaign_id: string;
  campaign_name: string | null;
  brand_name: string | null;
  company_name: string | null;
  email: string | null;
  total_budget: number | null;
  start_date: string;
  end_date: string;
  status: string;
  created_at: string;
  selected_locations?: any;
}

interface AffiliateBookingsTabProps {
  bookings: Booking[];
  onViewInvoice?: (campaignId: string) => void;
  onViewPO?: (campaignId: string) => void;
}

export const AffiliateBookingsTab = ({ bookings, onViewInvoice, onViewPO }: AffiliateBookingsTabProps) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const statusMap: Record<string, { variant: "default" | "secondary" | "outline" | "destructive"; label: string }> = {
    pending: { variant: "outline", label: isRTL ? "معلق" : "Pending" },
    approved: { variant: "secondary", label: isRTL ? "تمت الموافقة" : "Approved" },
    rejected: { variant: "destructive", label: isRTL ? "مرفوض" : "Rejected" },
    active: { variant: "default", label: isRTL ? "نشط" : "Active" },
    completed: { variant: "default", label: isRTL ? "مكتمل" : "Completed" },
  };

  const filtered = bookings.filter(b => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.campaign_name?.toLowerCase().includes(q) ||
        b.brand_name?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q));
    }
    return true;
  });

  const now = new Date();
  const getCampaignProgress = (start: string, end: string) => {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    const n = now.getTime();
    if (n < s) return 0;
    if (n > e) return 100;
    return Math.round(((n - s) / (e - s)) * 100);
  };

  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">{isRTL ? "لا توجد حجوزات بعد" : "No bookings yet"}</p>
            <p className="text-sm mt-1">{isRTL ? "أنشئ حملة نيابة عن عميل للبدء" : "Create a campaign on behalf of a client to get started"}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle>{isRTL ? "حجوزاتي" : "My Bookings"} ({filtered.length})</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={isRTL ? "بحث..." : "Search..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 w-[180px]"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? "الكل" : "All"}</SelectItem>
                <SelectItem value="pending">{isRTL ? "معلق" : "Pending"}</SelectItem>
                <SelectItem value="approved">{isRTL ? "موافق" : "Approved"}</SelectItem>
                <SelectItem value="active">{isRTL ? "نشط" : "Active"}</SelectItem>
                <SelectItem value="completed">{isRTL ? "مكتمل" : "Completed"}</SelectItem>
                <SelectItem value="rejected">{isRTL ? "مرفوض" : "Rejected"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>{isRTL ? "الحملة" : "Campaign"}</TableHead>
              <TableHead>{isRTL ? "العميل" : "Client"}</TableHead>
              <TableHead>{isRTL ? "الميزانية" : "Budget"}</TableHead>
              <TableHead>{isRTL ? "التواريخ" : "Dates"}</TableHead>
              <TableHead>{isRTL ? "التقدم" : "Progress"}</TableHead>
              <TableHead>{isRTL ? "الحالة" : "Status"}</TableHead>
              <TableHead>{isRTL ? "المستندات" : "Docs"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((booking) => {
              const progress = getCampaignProgress(booking.start_date, booking.end_date);
              const s = statusMap[booking.status] || { variant: "outline" as const, label: booking.status };
              const isExpanded = expandedId === booking.id;

              return (
                <Collapsible key={booking.id} open={isExpanded} onOpenChange={() => setExpandedId(isExpanded ? null : booking.id)} asChild>
                  <>
                    <CollapsibleTrigger asChild>
                      <TableRow className="cursor-pointer hover:bg-muted/50">
                        <TableCell className="w-8">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="font-medium">
                          {booking.campaign_name || booking.brand_name || '-'}
                        </TableCell>
                        <TableCell>{booking.email || '-'}</TableCell>
                        <TableCell>{booking.total_budget?.toLocaleString() || 0} SAR</TableCell>
                        <TableCell className="text-xs">
                          {new Date(booking.start_date).toLocaleDateString()} - {new Date(booking.end_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{progress}%</span>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant={s.variant}>{s.label}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {onViewInvoice && (
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onViewInvoice(booking.campaign_id); }}>
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {onViewPO && (
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onViewPO(booking.campaign_id); }}>
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    </CollapsibleTrigger>
                    <CollapsibleContent asChild>
                      <TableRow>
                        <TableCell colSpan={8}>
                          <div className="py-3 px-4 bg-muted/30 rounded-md space-y-2">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">{isRTL ? "الشركة" : "Company"}</p>
                                <p className="font-medium">{booking.company_name || '-'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">{isRTL ? "البريد" : "Email"}</p>
                                <p className="font-medium">{booking.email || '-'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">{isRTL ? "تاريخ الإنشاء" : "Created"}</p>
                                <p className="font-medium">{new Date(booking.created_at).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">{isRTL ? "معرف الحملة" : "Campaign ID"}</p>
                                <p className="font-mono text-xs">{booking.campaign_id.substring(0, 8)}...</p>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
